/*
	backup_file_download.js — shared helper for downloading an encrypted wallet
	backup file to disk (Electron) or triggering a browser download (web).

	Extracted from wallet_password.js so it can be reused by:
		- the create-password page (wallet_password.js)
		- the settings "Export backup file" button (Slide_Settings.js)
		- the "Change password" flow's optional re-export step

	The Electron path also saves a local copy in the app path cache via
	save_localcache_encrypted_wallet_backup() for faster future reopenings.
*/

import Constants, { appConfig } from "../config.js"
import { getLog } from "./helpers.js"
import { save_localcache_encrypted_wallet_backup } from "../Contexts/UserContext/UserContext.js"


function savefile_browsermode(filename, data) {
	var blob = new Blob([data], { type: "text/csv" })
	if (window.navigator.msSaveOrOpenBlob) {
		window.navigator.msSaveBlob(blob, filename)
	} else {
		var elem = window.document.createElement("a")
		elem.href = window.URL.createObjectURL(blob)
		elem.download = filename
		document.body.appendChild(elem)
		elem.click()
		document.body.removeChild(elem)
	}
}


/**
 * Generates the encrypted backup file (via the provided generateBackupFile
 * function — must be async and return the blob object) and saves it to disk
 * (Electron save dialog) or triggers a browser download (web).
 *
 * @param {string} password                the wallet password (passed to generateBackupFile)
 * @param {string} walletname              used to build the filename
 * @param {string} address_owner           owner address, used for the local cache copy (Electron)
 * @param {() => Promise<object>} generateBackupFileFn  async function returning the backup blob
 * @param {object} [opts]
 * @param {string} [opts.filenamePrefix="TokkenR_wallet_"]
 * @param {string} [opts.extension]        defaults to ".<appConfig.name.toLowerCase()>"
 * @returns {Promise<{success: boolean, error?: string}>}
 */
export async function downloadEncryptedBackupFile(password, walletname, address_owner, generateBackupFileFn, opts) {
	const function_id = "downloadEncryptedBackupFile(): "
	const log = getLog(function_id)

	if (!password) {
		const err = function_id + "password is required"
		log(err)
		return { success: false, error: err }
	}

	const filenamePrefix = (opts && opts.filenamePrefix) || "TokkenR_wallet_"
	const extension = (opts && opts.extension) || ("." + appConfig.name.toLowerCase())
	const filename = filenamePrefix + (walletname || "") + extension

	try {
		let data = await generateBackupFileFn(password)
		data = JSON.stringify(data)

		if (!Constants.IS_WEB_BUILD) {
			// Electron: show save dialog
			let defaultpath = window.electron.getAppGetPath('documents') + "/"

			let savePath = window.electron.showSaveDialogSync({
				defaultPath: defaultpath + filename,
				properties: ['createDirectory', 'showOverwriteConfirmation']
			})

			if (!savePath) {
				const err = function_id + "showSaveDialogSync(): no path returned (user cancelled?)"
				log(err)
				return { success: false, error: err }
			}

			log("savePath:", savePath)

			try {
				await window.electron.writeFileSync(savePath, data)
			} catch (err) {
				const errmess = function_id + 'an error ocurred with file saving ' + err.message
				window.electron.openDialogShowMessageBox({ title: "Erreur", message: errmess })
				log(errmess)
				return { success: false, error: errmess }
			}

			// save a local copy in the app path cache for future reopenings
			if (address_owner) {
				log("saving backup in local cache, address=", address_owner)
				save_localcache_encrypted_wallet_backup(address_owner, data)
			} else {
				console.error(function_id + "address_owner is not set.")
			}

			let message = "Fichier sauvegardé avec succès: " + filename
			window.electron.openDialogShowMessageBox({ title: "Information", message: message })
		} else {
			// Web: trigger browser download
			savefile_browsermode(filename, data)
		}

		return { success: true }
	} catch (err) {
		const errmess = function_id + (err && err.message ? err.message : String(err))
		log(errmess)
		return { success: false, error: errmess }
	}
}
